<template>
  <modal
    :show.sync="showModal"
    id="giftCardAdjustValueModal"
    body-classes="p-0"
    modal-classes="modal-dialog-centered modal-md"
    @close="onClose"
  >
    <card
      type="secondary"
      header-classes="bg-white pb-5"
      body-classes="px-lg-5 py-lg-5"
      class="border-0 mb-0"
    >
      <h4 class="card-title">Adjust value</h4>

      <!-- Current state -->
      <div class="gc-state-row mb-1" v-if="giftCard">
        <div class="gc-state-item gc-state-code">
          <span class="gc-state-label text-white">Code</span>
          <span class="gc-state-value text-white"><code style="color: inherit">{{ formattedCode }}</code></span>
        </div>
        <div class="gc-state-item">
          <span class="gc-state-label text-white">Value</span>
          <span class="gc-state-value text-white">{{ getAmount(giftCard.total) }}</span>
        </div>
        <div class="gc-state-item">
          <span class="gc-state-label text-white">Price</span>
          <span class="gc-state-value text-white">{{ getAmount(giftCard.price) }}</span>
        </div>
        <div class="gc-state-item">
          <span class="gc-state-label text-white">Remaining</span>
          <span class="gc-state-value text-primary" style="font-size: 1.1em; font-weight: 600;">
            {{ getAmount(giftCard.remaining) }}
          </span>
        </div>
      </div>

      <!-- Direction toggle -->
      <div class="mt-3 mb-1">
        <label class="text-white">Adjustment type <span class="text-danger">*</span></label>
        <div>
          <el-radio-group v-model="adjustmentType" size="small" class="adjustment-toggle">
            <el-radio-button label="increase">
              <i class="tim-icons icon-simple-add mr-1"></i> Increase
            </el-radio-button>
            <el-radio-button label="decrease">
              <i class="tim-icons icon-simple-delete mr-1"></i> Decrease
            </el-radio-button>
          </el-radio-group>
        </div>
        <div v-if="submitted && !adjustmentType" class="text-danger mt-1" style="font-size: 12px;">
          Select increase or decrease
        </div>
      </div>

      <!-- Amount input -->
      <div class="mb-1">
        <label class="text-white">Amount <span class="text-danger">*</span></label>
        <div class="amount-input-group" :class="{ 'has-error': submitted && amountError }">
          <span class="amount-prefix">{{ currencySymbol }}</span>
          <base-input class="amount-input-wrapper mb-0">
            <el-input-number
              :controls="false"
              v-model="adjustmentAmount"
              :min="0"
              :precision="2"
              placeholder="0.00"
            />
          </base-input>
        </div>
        <div v-if="submitted && amountError" class="text-danger mt-1" style="font-size: 12px;">
          {{ amountError }}
        </div>
      </div>

      <!-- Live preview -->
      <div class="adjustment-preview" v-if="adjustmentType && adjustmentAmount > 0" aria-live="polite" aria-atomic="true">
        <div class="preview-row">
          <span class="preview-label text-white">New remaining balance</span>
          <span class="preview-value" :class="wouldGoNegative ? 'text-danger' : (adjustmentType === 'increase' ? 'text-success' : 'text-warning')">
            {{ getAmount(newRemaining) }}
          </span>
        </div>
        <div class="preview-change">
          <span class="text-white" style="opacity: 0.6; font-size: 12px;">
            {{ getAmount(currentRemaining) }}
            <i class="tim-icons icon-minimal-right mx-1" style="font-size: 10px;"></i>
            {{ getAmount(newRemaining) }}
          </span>
        </div>
      </div>

      <!-- Large adjustment warning -->
      <div v-if="isLargeAdjustment" class="large-adjustment-warning mt-3">
        <i class="tim-icons icon-alert-circle-exc mr-2"></i>
        <span>This adjustment {{ adjustmentType === 'increase' ? 'will more than double' : 'exceeds' }} the original card value.</span>
      </div>

      <!-- Reason -->
      <div class="mt-2 mb-1">
        <label class="text-white">Reason for adjustment <span class="text-danger">*</span></label>
        <el-input
          type="textarea"
          v-model="reason"
          :rows="2"
          placeholder="e.g. Correcting data entry error, price match, goodwill credit"
        />
        <div v-if="submitted && reasonError" class="text-danger mt-1" style="font-size: 12px;">
          {{ reasonError }}
        </div>
      </div>

      <!-- Footer -->
      <div class="row mt-3">
        <div class="col text-center">
          <base-button type="default" @click="onClose" class="mr-2">Cancel</base-button>
          <base-button
            type="primary"
            :disabled="!canSubmit"
            :loading="submitting"
            @click="submit"
          >
            <template v-if="adjustmentType && adjustmentAmount > 0 && !wouldGoNegative">
              {{ adjustmentType === 'increase' ? 'Increase' : 'Decrease' }} by {{ getAmount(adjustmentAmount) }}
            </template>
            <template v-else>
              Submit adjustment
            </template>
          </base-button>
        </div>
      </div>
    </card>
  </modal>
</template>

<script>
import { Modal } from '@/components';
import { RadioGroup, RadioButton, InputNumber, Input } from 'element-ui';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import swal from 'sweetalert2';
import currencies from '@/assets/currencies.json';
import _ from 'lodash';

export default {
  name: 'GiftCardAdjustValueModal',

  components: {
    Modal,
    [RadioGroup.name]: RadioGroup,
    [RadioButton.name]: RadioButton,
    [InputNumber.name]: InputNumber,
    [Input.name]: Input
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    giftCard: {
      type: Object,
      default: () => null
    },
    currencySymbol: {
      type: String,
      default: '$'
    },
    currency: {
      type: String,
      default: 'USD'
    },
    currencyFormat: {
      type: Number,
      default: 0
    },
    numberFormat: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      currencies: currencies.currencies,
      adjustmentType: '',
      adjustmentAmount: null,
      reason: '',
      submitting: false,
      submitted: false
    };
  },

  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },

    formattedCode() {
      if (!this.giftCard || !this.giftCard.code) return '';
      const code = this.giftCard.code;
      if (isNaN(code)) return code;
      return code.match(/.{1,4}/g).join(' ');
    },

    currentRemaining() {
      return this.giftCard ? parseFloat(this.giftCard.remaining) : 0;
    },

    currentTotal() {
      return this.giftCard ? parseFloat(this.giftCard.total) : 0;
    },

    delta() {
      const amount = parseFloat(this.adjustmentAmount) || 0;
      return this.adjustmentType === 'decrease' ? -amount : amount;
    },

    newRemaining() {
      return this.currentRemaining + this.delta;
    },

    newTotal() {
      return this.currentTotal + this.delta;
    },

    wouldGoNegative() {
      return this.adjustmentType === 'decrease' && this.newRemaining < 0;
    },

    isLargeAdjustment() {
      if (!this.adjustmentAmount || !this.adjustmentType) return false;
      return this.adjustmentAmount > this.currentTotal;
    },

    amountError() {
      if (!this.adjustmentAmount || this.adjustmentAmount <= 0) {
        return 'Enter an adjustment amount';
      }
      if (this.wouldGoNegative) {
        return `Amount exceeds remaining balance of ${this.getAmount(this.currentRemaining)}`;
      }
      return '';
    },

    reasonError() {
      if (!this.reason || this.reason.trim().length < 3) {
        return 'Provide a reason for this adjustment (at least 3 characters)';
      }
      return '';
    },

    canSubmit() {
      return this.adjustmentType
        && this.adjustmentAmount > 0
        && !this.wouldGoNegative
        && this.reason.trim().length >= 3
        && !this.submitting;
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.adjustmentType = '';
        this.adjustmentAmount = null;
        this.reason = '';
        this.submitted = false;
        this.submitting = false;
      }
    }
  },

  methods: {
    onClose() {
      this.$emit('close');
      this.showModal = false;
    },

    getAmount(value) {
      if (value == null) return '';

      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;

      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currencySymbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.numberFormat === 1) {
        formattedAmount = numValue.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        formattedAmount = parseFloat(numValue).toFixed(2);
      }

      if (this.currencyFormat === 0) {
        if (isPostfix) {
          return formattedAmount + symbol;
        } else {
          return symbol + formattedAmount;
        }
      } else {
        return formattedAmount + ' ' + this.currency;
      }
    },

    async getAuthToken() {
      const user = await Auth.currentAuthenticatedUser();
      return user.signInUserSession.idToken.jwtToken;
    },

    async submit() {
      this.submitted = true;

      if (!this.canSubmit) return;

      this.submitting = true;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/gifts/' + this.giftCard.id;

        const data = {
          adjustment_amount: this.delta,
          reason: this.reason.trim()
        };

        await axios.put(url, data, config);

        this.$emit('adjusted');
        this.onClose();

        swal('Success', 'Gift card value has been adjusted', 'success');
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error'
          }).then(() => {
            this.$router.push('/');
          });
        } else if (err.response && err.response.data) {
          swal('Error', err.response.data, 'error');
        } else {
          swal('Error', 'Something went wrong', 'error');
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.gc-state-row {
  display: flex;
  gap: 24px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.4285rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
}

.gc-state-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gc-state-code {
  flex: 1 1 100%;
  padding-bottom: 6px;
  margin-bottom: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.gc-state-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
}

.gc-state-value {
  font-size: 14px;
}

.adjustment-toggle {
  width: 100%;
}

::v-deep .adjustment-toggle .el-radio-button__inner {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
}

::v-deep .adjustment-toggle .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  border: 1px solid #e14eca;
  background: rgba(225, 78, 202, 0.15);
  color: #ffffff;
  box-shadow: none;
}

.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  width: 200px;
}

.amount-input-group:focus-within {
  border-color: #e14eca;
}

.amount-input-group.has-error {
  border-color: #ec250d;
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;
}

::v-deep .amount-input-wrapper .form-control {
  border: none;
  border-radius: 0;
  background: transparent;
}

::v-deep .amount-input-wrapper .el-input-number {
  width: 100%;
}

::v-deep .amount-input-wrapper .el-input-number .el-input__inner {
  text-align: left;
  color: white;
  background: transparent;
  border: none;
}

.adjustment-preview {
  padding: 10px 14px;
  background: rgba(225, 78, 202, 0.08);
  border: 1px solid rgba(225, 78, 202, 0.3);
  border-radius: 0.4285rem;
  margin-top: 14px;
  margin-bottom: 0;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-label {
  font-size: 13px;
}

.preview-value {
  font-size: 1.25em;
  font-weight: 600;
}

.preview-change {
  margin-top: 4px;
}

.large-adjustment-warning {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: rgba(255, 178, 43, 0.1);
  border: 1px solid rgba(255, 178, 43, 0.3);
  border-radius: 0.4285rem;
  color: #ffb22b;
  font-size: 13px;
  margin-bottom: 12px;
}

/* Dark mode support for Black Dashboard theme */
::v-deep .card.card-secondary {
  background-color: #22233a;
}

::v-deep .card.card-secondary .card-title {
  color: #ffffff;
}

::v-deep .el-textarea__inner {
  background-color: rgba(30, 30, 47, 0.6);
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

::v-deep .el-textarea__inner:focus {
  border-color: #e14eca;
}

::v-deep .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

@media (max-width: 576px) {
  .amount-input-group {
    width: 100%;
  }

  .gc-state-row {
    gap: 12px;
  }

  .gc-state-item {
    flex: 1 1 calc(33% - 12px);
  }

  .gc-state-code {
    flex: 1 1 100%;
  }
}
</style>

<style>
/* Light mode overrides — unscoped so .white-content ancestor selector works */
.white-content .gc-state-row {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .gc-state-code {
  border-bottom-color: rgba(0, 0, 0, 0.08);
}

.white-content .gc-state-label {
  color: rgba(0, 0, 0, 0.5);
}

.white-content .gc-state-value {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .gc-state-value code {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .adjustment-toggle .el-radio-button__inner {
  border-color: rgba(0, 0, 0, 0.15);
  color: rgba(0, 0, 0, 0.6);
}

.white-content .adjustment-toggle .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  border: 1px solid #e14eca;
  background: rgba(225, 78, 202, 0.1);
  color: #d63384;
  box-shadow: none;
}

.white-content #giftCardAdjustValueModal .amount-input-group {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(225, 78, 202, 0.3);
}

.white-content #giftCardAdjustValueModal .amount-input-wrapper .el-input-number .el-input__inner {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .adjustment-preview {
  background: rgba(225, 78, 202, 0.05);
  border-color: rgba(225, 78, 202, 0.2);
}

.white-content .preview-label {
  color: rgba(0, 0, 0, 0.7);
}

.white-content .preview-change span {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .large-adjustment-warning {
  background: rgba(255, 178, 43, 0.08);
  border-color: rgba(255, 178, 43, 0.25);
  color: #b47800;
}

.white-content #giftCardAdjustValueModal .el-textarea__inner {
  background-color: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.15);
  color: rgba(0, 0, 0, 0.85);
}

.white-content #giftCardAdjustValueModal .el-textarea__inner:focus {
  border-color: #e14eca;
}

.white-content #giftCardAdjustValueModal .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.white-content #giftCardAdjustValueModal .card.card-secondary {
  background-color: #ffffff;
}

.white-content #giftCardAdjustValueModal .card-title {
  color: rgba(0, 0, 0, 0.85);
}

.white-content #giftCardAdjustValueModal label {
  color: rgba(0, 0, 0, 0.7);
}
</style>
